/**
 * Product Queries
 *
 * React Query hooks for product catalog management.
 * When REACT_APP_USE_GOBODO_API is true, uses Stripe product API via stripeProductService.
 * Otherwise uses mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedProductListViews, persistedProductRecords } from '../mockData/products';
import * as stripeProductService from '../stripeProductService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { ProductCatalog } from '../../types/index';
import type { ListView, ListViewData, ListViewRecord } from '../../types/listView';

/**
 * Fetch all products from the active source (Stripe API when Gobodo on, else localStorage).
 * Use from queryFns or when you need a product list outside of useProducts().
 */
export async function fetchProductsForQuery(): Promise<ProductCatalog[]> {
  if (shouldUseGobodo()) {
    return stripeProductService.listProducts();
  }
  await new Promise(resolve => setTimeout(resolve, 300));
  return persistedProductRecords.get();
}

/**
 * Get all products
 */
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: fetchProductsForQuery,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get single product by ID
 */
export function useProduct(id: string) {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        return stripeProductService.getProduct(id);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const products = persistedProductRecords.get();
      const product = products.find(p => p.id === id);
      if (!product) throw new Error('Product not found');
      return product;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create product mutation
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (product: Omit<ProductCatalog, 'id' | 'createdDate' | 'lastModifiedDate' | 'createdBy' | 'lastModifiedBy'>) => {
      if (shouldUseGobodo()) {
        return stripeProductService.createProduct(product);
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const newProduct: ProductCatalog = {
        ...product,
        id: `prod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdDate: new Date().toISOString(),
        lastModifiedDate: new Date().toISOString(),
        createdBy: 'current_user', // TODO: Get from auth context
        lastModifiedBy: 'current_user'
      };

      persistedProductRecords.add(newProduct);
      return newProduct;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['productListViews'] });
    },
  });
}

/**
 * Update product mutation
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ProductCatalog> }) => {
      if (shouldUseGobodo()) {
        return stripeProductService.updateProduct(id, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 500));

      const updated = persistedProductRecords.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString(),
        lastModifiedBy: 'current_user'
      });

      if (!updated) throw new Error('Product not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', data.id] });
      queryClient.invalidateQueries({ queryKey: ['productListViews'] });
    },
  });
}

/**
 * Delete product mutation
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (shouldUseGobodo()) {
        await stripeProductService.deleteProduct(id);
        return id;
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const success = persistedProductRecords.remove(id);
      if (!success) throw new Error('Product not found');
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['productListViews'] });
    },
  });
}

/**
 * Get available list views for products
 */
export function useProductListViews() {
  return useQuery({
    queryKey: ['productListViews'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedProductListViews.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get list view data (view configuration + records)
 */
export function useProductListViewData(listViewId: string) {
  return useQuery<ListViewData>({
    queryKey: ['productListViewData', listViewId],
    queryFn: async () => {
      const allViews = persistedProductListViews.get();
      const allRecords = (await fetchProductsForQuery()) as ListViewRecord[];

      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) {
        throw new Error('List view not found');
      }

      // Apply filters if any
      let filteredRecords = [...allRecords];

      if (listView.filters && listView.filters.length > 0) {
        listView.filters.forEach(filter => {
          filteredRecords = filteredRecords.filter(record => {
            const fieldValue = record[filter.fieldApiName];

            switch (filter.operator) {
              case 'equals':
                return fieldValue === filter.value;
              case 'notEquals':
                return fieldValue !== filter.value;
              case 'contains':
                return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith':
                return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default:
                return true;
            }
          });
        });
      }

      // Apply sorting if specified
      if (listView.sortBy) {
        filteredRecords.sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];

          if (listView.sortBy!.direction === 'asc') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
      }

      return {
        listView,
        records: filteredRecords,
        totalRecords: filteredRecords.length
      };
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!listViewId,
  });
}

/**
 * Get default list view for products
 */
export function useDefaultProductListView() {
  return useQuery({
    queryKey: ['defaultProductListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      const allViews = persistedProductListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);
      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update a product list view
 */
export function useUpdateProductListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      await new Promise(resolve => setTimeout(resolve, 500));
      const updated = persistedProductListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });
      if (!updated) throw new Error('List view not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['productListViews'] });
      queryClient.invalidateQueries({ queryKey: ['productListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultProductListView'] });
    },
  });
}
